import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  IconChevronLeft,
  IconShip,
  IconMapPin,
  IconUsers,
  IconClipboardList,
  IconInfoCircle,
  IconEye,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import useGetPartnerTrips from '../../../hooks/useGetPartnerTrips';
import useGetPartnerBookings from '../../../hooks/useGetPartnerBookings';
import useIsAdmin from '../../../hooks/useIsAdmin';
import useGetBoatRooms from '../../../hooks/useGetBoatRooms';
import useUpdateTripPrices from '../../../hooks/useUpdateTripPrices';
import useUpdateTripAvailability from '../../../hooks/useUpdateTripAvailability';
import { getErrorMessage } from '../../../utils/errorUtils';
import { format } from 'date-fns';
import BookingStatusBadge from '../Bookings/components/BookingStatusBadge';
import { getTripStatus } from '../../../constants/tripStatus';
import TripDeleteDialog from '../../../components/Partner/TripDeleteDialog';

const TripDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tripId = parseInt(id || '0', 10);

  const {
    trips,
    isLoading: tripsLoading,
    isError: tripsError,
    error: tripsErrorDetails,
  } = useGetPartnerTrips();

  const {
    bookings,
    isLoading: bookingsLoading,
    isError: bookingsError,
    error: bookingsErrorDetails,
  } = useGetPartnerBookings();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Admin edit state
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [priceEdits, setPriceEdits] = useState<Record<number, string>>({});
  const [availabilityEdits, setAvailabilityEdits] = useState<
    Record<number, string>
  >({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success',
  );

  // Refs to track focused inputs and restore focus after re-render
  const priceInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const availabilityInputRefs = useRef<Record<number, HTMLInputElement | null>>(
    {},
  );
  const focusedPriceInputId = useRef<number | null>(null);
  const focusedAvailabilityInputId = useRef<number | null>(null);
  const isTabbingRef = useRef<boolean>(false);

  const queryClient = useQueryClient();
  const updatePricesMutation = useUpdateTripPrices();
  const updateAvailabilityMutation = useUpdateTripAvailability();

  const isLoading = tripsLoading || bookingsLoading || isAdminLoading;
  const hasError = tripsError || bookingsError;

  // Find the trip
  const trip = useMemo(() => {
    return trips.find(t => t.tripId === tripId);
  }, [trips, tripId]);

  // Fetch room types if admin (after trip is defined)
  const { boatRooms, boatRoomsLoading } = useGetBoatRooms(
    trip?.boatId || 0,
    tripId,
  );

  // Filter bookings for this trip
  const tripBookings = useMemo(() => {
    return bookings.filter(booking => booking.tripId === tripId);
  }, [bookings, tripId]);

  // Calculate total guests
  const totalGuests = useMemo(() => {
    return tripBookings.reduce((sum, booking) => sum + booking.guestCount, 0);
  }, [tripBookings]);

  // Get trip status
  const tripStatus = trip
    ? getTripStatus({
        embarkationDate: trip.embarkationDate,
        disembarkationDate: trip.disembarkationDate,
      })
    : null;

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    // Navigate back to trips list after successful deletion
    navigate('/partner/trips');
  };

  // Initialize edit state when room data loads
  useEffect(() => {
    if (boatRooms?.roomTypes && isAdmin) {
      const initialPrices: Record<number, string> = {};
      const initialAvailability: Record<number, string> = {};
      boatRooms.roomTypes.forEach(room => {
        initialPrices[room.typeId] = room.price.toString();
        initialAvailability[room.typeId] = room.totalAvailable.toString();
      });
      setPriceEdits(initialPrices);
      setAvailabilityEdits(initialAvailability);
    }
  }, [boatRooms, isAdmin]);

  // Restore focus after re-render
  useEffect(() => {
    if (
      focusedPriceInputId.current !== null &&
      priceInputRefs.current[focusedPriceInputId.current]
    ) {
      const input = priceInputRefs.current[focusedPriceInputId.current];
      if (input) {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          input.focus();
          // Restore cursor position if possible
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }, 0);
      }
    }
  }, [priceEdits]);

  useEffect(() => {
    if (
      focusedAvailabilityInputId.current !== null &&
      availabilityInputRefs.current[focusedAvailabilityInputId.current]
    ) {
      const input =
        availabilityInputRefs.current[focusedAvailabilityInputId.current];
      if (input) {
        setTimeout(() => {
          input.focus();
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }, 0);
      }
    }
  }, [availabilityEdits]);

  const handleStartEditPrices = () => {
    setIsEditingPrices(true);
  };

  const handleCancelEditPrices = () => {
    setIsEditingPrices(false);
    // Reset to original values
    if (boatRooms?.roomTypes) {
      const originalPrices: Record<number, string> = {};
      boatRooms.roomTypes.forEach(room => {
        originalPrices[room.typeId] = room.price.toString();
      });
      setPriceEdits(originalPrices);
    }
  };

  const handleSavePrices = async () => {
    if (!trip) return;

    try {
      const roomPrices = Object.entries(priceEdits).map(
        ([typeId, priceStr]) => ({
          typeId: parseInt(typeId),
          // Round to 2 decimal places for currency precision
          price: Math.round((parseFloat(priceStr) || 0) * 100) / 100,
        }),
      );

      await updatePricesMutation.mutateAsync({
        tripId: trip.tripId,
        roomPrices,
      });

      // Update React Query cache optimistically
      if (boatRooms?.roomTypes && trip?.boatId) {
        queryClient.setQueryData(
          ['boatRooms', trip.boatId, trip.tripId],
          (oldData: typeof boatRooms | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              roomTypes: oldData.roomTypes.map(room => ({
                ...room,
                // Round to 2 decimal places for currency precision
                price:
                  Math.round(
                    (parseFloat(priceEdits[room.typeId]) || room.price) * 100,
                  ) / 100,
              })),
            };
          },
        );
      }

      setIsEditingPrices(false);
      setSnackbarMessage('Prices updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      // Rollback optimistic update on error
      if (trip?.boatId) {
        queryClient.invalidateQueries({
          queryKey: ['boatRooms', trip.boatId, trip.tripId],
        });
      }
      setSnackbarMessage(getErrorMessage(error));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleStartEditAvailability = () => {
    setIsEditingAvailability(true);
  };

  const handleCancelEditAvailability = () => {
    setIsEditingAvailability(false);
    // Reset to original values
    if (boatRooms?.roomTypes) {
      const originalAvailability: Record<number, string> = {};
      boatRooms.roomTypes.forEach(room => {
        originalAvailability[room.typeId] = room.totalAvailable.toString();
      });
      setAvailabilityEdits(originalAvailability);
    }
  };

  const handleSaveAvailability = async () => {
    if (!trip) return;

    try {
      const roomAvailabilities = Object.entries(availabilityEdits).map(
        ([typeId, availableRoomsStr]) => ({
          roomId: parseInt(typeId), // roomId in DB corresponds to typeId
          availableRooms: parseInt(availableRoomsStr) || 0,
        }),
      );

      await updateAvailabilityMutation.mutateAsync({
        tripId: trip.tripId,
        roomAvailabilities,
      });

      // Update React Query cache optimistically
      if (boatRooms?.roomTypes && trip?.boatId) {
        queryClient.setQueryData(
          ['boatRooms', trip.boatId, trip.tripId],
          (oldData: typeof boatRooms | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              roomTypes: oldData.roomTypes.map(room => ({
                ...room,
                totalAvailable:
                  parseInt(availabilityEdits[room.typeId]) ||
                  room.totalAvailable,
              })),
            };
          },
        );
      }

      setIsEditingAvailability(false);
      setSnackbarMessage('Availability updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      // Rollback optimistic update on error
      if (trip?.boatId) {
        queryClient.invalidateQueries({
          queryKey: ['boatRooms', trip.boatId, trip.tripId],
        });
      }
      setSnackbarMessage(getErrorMessage(error));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Calculate total availability
  const totalAvailability = useMemo(() => {
    if (!boatRooms?.roomTypes) return 0;
    return boatRooms.roomTypes.reduce((sum, room) => {
      if (isEditingAvailability) {
        return sum + (parseInt(availabilityEdits[room.typeId]) || 0);
      }
      return sum + room.totalAvailable;
    }, 0);
  }, [boatRooms, isEditingAvailability, availabilityEdits]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading trip</div>
          <div className="mt-2 text-sm">
            {getErrorMessage(tripsErrorDetails || bookingsErrorDetails)}
          </div>
        </Alert>
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/trips')}
        >
          Back to Trips
        </Button>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="warning" className="mb-4">
          Trip not found
        </Alert>
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/trips')}
        >
          Back to Trips
        </Button>
      </div>
    );
  }

  const InfoSection: FC<{
    title: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
  }> = ({ title, icon, children }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        {icon && <div className="text-gray-600">{icon}</div>}
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      <dl className="space-y-3">{children}</dl>
    </div>
  );

  const InfoRow: FC<{
    label: string;
    value: string | number | null | undefined;
  }> = ({ label, value }) => (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value !== null && value !== undefined ? value : 'N/A'}
      </dd>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/trips')}
          className="mb-4"
        >
          Back to Trips
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Trip #{trip.tripId}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View trip information and associated bookings
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outlined"
              color="error"
              startIcon={<IconTrash className="h-4 w-4" />}
              onClick={handleDeleteClick}
            >
              Delete Trip
            </Button>
            {tripStatus && (
              <Chip
                label={tripStatus.label}
                color={tripStatus.color}
                size="medium"
              />
            )}
          </div>
        </div>
      </div>

      {/* Trip Information */}
      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <InfoSection
          title="Trip Information"
          icon={<IconShip className="h-5 w-5" />}
        >
          <InfoRow label="Trip ID" value={trip.tripId} />
          <InfoRow label="Boat Name" value={trip.boatName} />
          <InfoRow label="Route Name" value={trip.routeName} />
          <InfoRow
            label="Embarkation Date"
            value={format(
              new Date(trip.embarkationDate),
              'EEEE, MMMM dd, yyyy',
            )}
          />
          <InfoRow
            label="Disembarkation Date"
            value={format(
              new Date(trip.disembarkationDate),
              'EEEE, MMMM dd, yyyy',
            )}
          />
          {trip.diveCount && (
            <InfoRow label="Number of Dives" value={trip.diveCount} />
          )}
        </InfoSection>

        <InfoSection
          title="Route Information"
          icon={<IconMapPin className="h-5 w-5" />}
        >
          <InfoRow label="Route ID" value={trip.routeId} />
          <InfoRow label="Route Name" value={trip.routeName} />
          <InfoRow label="Boat ID" value={trip.boatId} />
          <InfoRow label="Organizer ID" value={trip.organizerId} />
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-start gap-2">
              <IconInfoCircle className="mt-0.5 h-4 w-4 text-blue-600" />
              <p className="text-xs text-blue-800">
                Detailed route information, dive sites, and itinerary details
                may be available through additional queries or the public trip
                page.
              </p>
            </div>
          </div>
        </InfoSection>
      </div>

      {/* Associated Bookings */}
      <div className="mb-6">
        <InfoSection
          title={`Associated Bookings (${tripBookings.length})`}
          icon={<IconClipboardList className="h-5 w-5" />}
        >
          {tripBookings.length === 0 ? (
            <div className="py-8 text-center">
              <IconClipboardList className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-sm text-gray-500">
                No bookings found for this trip
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Guest Count</TableCell>
                    <TableCell>Dive Count</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tripBookings.map(booking => (
                    <TableRow key={booking.bookingId} hover>
                      <TableCell>{booking.bookingId}</TableCell>
                      <TableCell>
                        {booking.leadGuestFullName ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {booking.leadGuestFullName}
                            </div>
                            {booking.leadGuestEmail && (
                              <div className="text-xs text-gray-500">
                                {booking.leadGuestEmail}
                              </div>
                            )}
                            {booking.leadGuestPhone && (
                              <div className="text-xs text-gray-500">
                                {booking.leadGuestPhone}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <BookingStatusBadge status={booking.bookingStatus} />
                      </TableCell>
                      <TableCell>{booking.guestCount}</TableCell>
                      <TableCell>{booking.diveCount}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<IconEye className="h-4 w-4" />}
                          onClick={() =>
                            navigate(`/partner/bookings/${booking.bookingId}`)
                          }
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </InfoSection>
      </div>

      {/* Guest Summary */}
      <div className="mb-6">
        <InfoSection
          title="Guest Summary"
          icon={<IconUsers className="h-5 w-5" />}
        >
          <InfoRow label="Total Bookings" value={tripBookings.length} />
          <InfoRow label="Total Guests" value={totalGuests} />
          <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <div className="flex items-start gap-2">
              <IconInfoCircle className="mt-0.5 h-4 w-4 text-yellow-600" />
              <p className="text-xs text-yellow-800">
                Detailed guest information, room assignments, and guest lists
                are available in the individual booking detail pages. Click
                "View" on any booking above to see guest details.
              </p>
            </div>
          </div>
        </InfoSection>
      </div>

      {/* Admin: Room Prices and Availability Editor */}
      {isAdmin && trip && (
        <div className="mb-6">
          <InfoSection
            title="Room Prices & Availability"
            icon={<IconEdit className="h-5 w-5" />}
          >
            {boatRoomsLoading ? (
              <div className="flex items-center justify-center py-8">
                <CircularProgress size={24} />
              </div>
            ) : boatRooms?.roomTypes && boatRooms.roomTypes.length > 0 ? (
              <div className="space-y-4">
                {/* Prices Section */}
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      Room Prices
                    </h3>
                    {!isEditingPrices ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<IconEdit className="h-4 w-4" />}
                        onClick={handleStartEditPrices}
                      >
                        Edit Prices
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<IconCheck className="h-4 w-4" />}
                          onClick={handleSavePrices}
                          disabled={updatePricesMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<IconX className="h-4 w-4" />}
                          onClick={handleCancelEditPrices}
                          disabled={updatePricesMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room Type</TableCell>
                          <TableCell align="right">Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {boatRooms.roomTypes.map(room => (
                          <TableRow key={room.typeId}>
                            <TableCell>{room.typeName}</TableCell>
                            <TableCell align="right">
                              {isEditingPrices ? (
                                <TextField
                                  type="text"
                                  size="small"
                                  value={priceEdits[room.typeId] || ''}
                                  inputRef={el => {
                                    priceInputRefs.current[room.typeId] = el;
                                  }}
                                  onFocus={() => {
                                    focusedPriceInputId.current = room.typeId;
                                  }}
                                  onChange={e => {
                                    const value = e.target.value;
                                    // Allow empty string, integers, or single decimal point
                                    const decimalCount = (
                                      value.match(/\./g) || []
                                    ).length;
                                    if (
                                      value === '' ||
                                      (/^\d*\.?\d*$/.test(value) &&
                                        decimalCount <= 1)
                                    ) {
                                      // Use functional update to avoid dependency on previous state
                                      setPriceEdits(prev => ({
                                        ...prev,
                                        [room.typeId]: value,
                                      }));
                                    }
                                  }}
                                  onKeyDown={e => {
                                    // Mark that Tab key was pressed
                                    if (e.key === 'Tab') {
                                      isTabbingRef.current = true;
                                      // Clear focus tracking before tabbing
                                      if (
                                        focusedPriceInputId.current ===
                                        room.typeId
                                      ) {
                                        focusedPriceInputId.current = null;
                                      }
                                      // Reset the flag after a short delay to allow tab navigation
                                      setTimeout(() => {
                                        isTabbingRef.current = false;
                                      }, 100);
                                    }
                                  }}
                                  onBlur={e => {
                                    // Skip blur validation if Tab was pressed (focus is moving to another field)
                                    if (isTabbingRef.current) {
                                      if (
                                        focusedPriceInputId.current ===
                                        room.typeId
                                      ) {
                                        focusedPriceInputId.current = null;
                                      }
                                      return;
                                    }

                                    // Clear focus tracking
                                    if (
                                      focusedPriceInputId.current ===
                                      room.typeId
                                    ) {
                                      focusedPriceInputId.current = null;
                                    }
                                    // Ensure valid number on blur and round to 2 decimal places for currency
                                    const value = e.target.value;
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue) || numValue < 0) {
                                      setPriceEdits(prev => ({
                                        ...prev,
                                        [room.typeId]: room.price.toString(),
                                      }));
                                    } else {
                                      // Round to 2 decimal places for currency precision
                                      const roundedValue =
                                        Math.round(numValue * 100) / 100;
                                      setPriceEdits(prev => ({
                                        ...prev,
                                        [room.typeId]: roundedValue.toString(),
                                      }));
                                    }
                                  }}
                                  slotProps={{
                                    htmlInput: {
                                      min: 0,
                                      step: 0.01,
                                      tabIndex: 0,
                                    },
                                  }}
                                  sx={{ width: 120 }}
                                />
                              ) : (
                                `$${room.price.toLocaleString()}`
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Availability Section */}
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      Room Availability
                      {!isEditingAvailability && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          (Total: {totalAvailability})
                        </span>
                      )}
                    </h3>
                    {!isEditingAvailability ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<IconEdit className="h-4 w-4" />}
                        onClick={handleStartEditAvailability}
                      >
                        Edit Availability
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<IconCheck className="h-4 w-4" />}
                          onClick={handleSaveAvailability}
                          disabled={updateAvailabilityMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<IconX className="h-4 w-4" />}
                          onClick={handleCancelEditAvailability}
                          disabled={updateAvailabilityMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room Type</TableCell>
                          <TableCell align="right">Available Rooms</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {boatRooms.roomTypes.map(room => (
                          <TableRow key={room.typeId}>
                            <TableCell>{room.typeName}</TableCell>
                            <TableCell align="right">
                              {isEditingAvailability ? (
                                <TextField
                                  type="text"
                                  size="small"
                                  value={availabilityEdits[room.typeId] || ''}
                                  inputRef={el => {
                                    availabilityInputRefs.current[room.typeId] =
                                      el;
                                  }}
                                  onFocus={() => {
                                    focusedAvailabilityInputId.current =
                                      room.typeId;
                                  }}
                                  onChange={e => {
                                    const value = e.target.value;
                                    // Allow empty string or positive integers
                                    if (value === '' || /^\d+$/.test(value)) {
                                      // Use functional update to avoid dependency on previous state
                                      setAvailabilityEdits(prev => ({
                                        ...prev,
                                        [room.typeId]: value,
                                      }));
                                    }
                                  }}
                                  onKeyDown={e => {
                                    // Mark that Tab key was pressed
                                    if (e.key === 'Tab') {
                                      isTabbingRef.current = true;
                                      // Clear focus tracking before tabbing
                                      if (
                                        focusedAvailabilityInputId.current ===
                                        room.typeId
                                      ) {
                                        focusedAvailabilityInputId.current =
                                          null;
                                      }
                                      // Reset the flag after a short delay to allow tab navigation
                                      setTimeout(() => {
                                        isTabbingRef.current = false;
                                      }, 100);
                                    }
                                  }}
                                  onBlur={e => {
                                    // Skip blur validation if Tab was pressed (focus is moving to another field)
                                    if (isTabbingRef.current) {
                                      if (
                                        focusedAvailabilityInputId.current ===
                                        room.typeId
                                      ) {
                                        focusedAvailabilityInputId.current =
                                          null;
                                      }
                                      return;
                                    }

                                    // Clear focus tracking
                                    if (
                                      focusedAvailabilityInputId.current ===
                                      room.typeId
                                    ) {
                                      focusedAvailabilityInputId.current = null;
                                    }
                                    // Ensure valid number on blur
                                    const value = e.target.value;
                                    const intValue = parseInt(value);
                                    if (isNaN(intValue) || intValue < 0) {
                                      setAvailabilityEdits(prev => ({
                                        ...prev,
                                        [room.typeId]:
                                          room.totalAvailable.toString(),
                                      }));
                                    } else {
                                      setAvailabilityEdits(prev => ({
                                        ...prev,
                                        [room.typeId]: intValue.toString(),
                                      }));
                                    }
                                  }}
                                  slotProps={{
                                    htmlInput: { min: 0, tabIndex: 0 },
                                  }}
                                  sx={{ width: 120 }}
                                />
                              ) : (
                                room.totalAvailable
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {!isEditingAvailability && (
                          <TableRow>
                            <TableCell className="font-semibold">
                              Total
                            </TableCell>
                            <TableCell align="right" className="font-semibold">
                              {totalAvailability}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            ) : (
              <p className="py-4 text-sm text-gray-500">
                No room types found for this trip.
              </p>
            )}
          </InfoSection>
        </div>
      )}

      {/* Additional Information */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <IconInfoCircle className="h-5 w-5 text-gray-600" />
          Additional Information
        </h2>
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            <strong>Dive Sites:</strong> Dive site information is not available
            in the current trip summary. This information may be available
            through additional queries or on the public trip page.
          </p>
          <p>
            <strong>Room Availability:</strong> Room availability information
            requires additional queries. You can view room assignments in the
            individual booking detail pages.
          </p>
          <p>
            <strong>Transfer Information:</strong> Airport and pier transfer
            details are available in the associated booking detail pages.
          </p>
        </div>
      </div>

      {/* Delete Dialog */}
      <TripDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        trip={trip}
        onSuccess={handleDeleteSuccess}
        bookingCount={tripBookings.length}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TripDetail;
